import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store';

// ============================================
// Icons
// ============================================

const BellIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

// ============================================
// UserControls
// ============================================

interface UserControlsProps {
  /**
   * Lay the controls out as a row (top bar) or column (sidebar fallback).
   * Defaults to horizontal.
   */
  direction?: 'horizontal' | 'vertical';
}

/**
 * Shared bell / avatar cluster used by both the top page bar and
 * (as a fallback) the sidebar on full-screen builder pages.
 *
 * Clicking the avatar navigates to /settings — there is no popover.
 * Theme toggle and sign-out live in the Profile / Settings page, not here.
 */
const UserControls: React.FC<UserControlsProps> = ({ direction = 'horizontal' }) => {
  const { user } = useAuthStore();

  const initials = (user?.fullName || user?.email || '?')
    .split(' ')
    .map(s => s.trim()[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const wrapperClass =
    direction === 'vertical'
      ? 'flex flex-col items-center gap-1'
      : 'flex items-center gap-1';

  const buttonClass =
    'flex items-center justify-center w-9 h-9 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors';

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        title="Notifications"
        aria-label="Notifications"
        className={buttonClass}
      >
        <BellIcon />
      </button>

      <Link
        to="/settings"
        title={user?.fullName || user?.email || 'Account'}
        aria-label="Account settings"
        className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 transition-colors"
      >
        {initials}
      </Link>
    </div>
  );
};

export default UserControls;
